import { useCallback, useEffect, useRef, useState } from 'react';

import {
    BOTTOM_GAP,
    DEFAULT_HEIGHT,
    GAP_BELOW_NAVBAR,
    MIN_HEIGHT,
    STORAGE_KEY,
    TOP_NAVBAR_HEIGHT
} from '@/components/composite/SparksAgent/const';

export function useSparksAgentResize() {
    const [containerHeight, setContainerHeight] = useState(DEFAULT_HEIGHT);
    const [isResizing, setIsResizing] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const mouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
    const mouseUpRef = useRef<(() => void) | null>(null);

    const getMaxContainerHeight = useCallback(() => {
        return window.innerHeight - (TOP_NAVBAR_HEIGHT + GAP_BELOW_NAVBAR + BOTTOM_GAP);
    }, []);

    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            const startY = e.clientY;
            const startHeight = containerHeight;

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const delta = startY - moveEvent.clientY;

                const newHeight = Math.min(
                    Math.max(startHeight + delta, MIN_HEIGHT),
                    getMaxContainerHeight()
                );
                setContainerHeight(newHeight);
            };

            const handleMouseUp = () => {
                setIsResizing(false);
                setContainerHeight((h) => {
                    try {
                        localStorage.setItem(STORAGE_KEY, String(h));
                    } catch {
                        /* noop */
                    }
                    return h;
                });
                mouseMoveRef.current = null;
                mouseUpRef.current = null;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            mouseMoveRef.current = handleMouseMove;
            mouseUpRef.current = handleMouseUp;
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [containerHeight, getMaxContainerHeight]
    );

    useEffect(() => {
        try {
            const storedHeight = localStorage.getItem(STORAGE_KEY);
            if (storedHeight) {
                const parsed = parseFloat(storedHeight);

                if (!isNaN(parsed) && parsed >= MIN_HEIGHT)
                    setContainerHeight(Math.min(parsed, getMaxContainerHeight()));
            }
        } catch {
            /* localStorage unavailable */
        }
    }, [getMaxContainerHeight]);

    useEffect(() => {
        return () => {
            if (mouseMoveRef.current) {
                document.removeEventListener('mousemove', mouseMoveRef.current);
            }
            if (mouseUpRef.current) {
                document.removeEventListener('mouseup', mouseUpRef.current);
            }
        };
    }, []);

    return {
        containerRef,
        containerHeight,
        isResizing,
        handleResizeMouseDown
    };
}
